import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./DocumentPreview.scss";

// Bundle worker into the build output (hashed static asset). Avoids Azure/IIS
// 404s when /pdf.worker.min.mjs from public/ is missing or .mjs MIME is blocked.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.1;
/** Ignore tiny width changes (scrollbar show/hide) that re-render the page. */
const WIDTH_STABLE_PX = 12;

/**
 * Custom PDF preview (PDF.js) — continuous scroll of all pages.
 */
export default function PdfJsPreviewViewer({
  fileUrl = "",
  fileTitle = "Document",
  className = "",
  onLoadError,
}) {
  const wrapRef = useRef(null);
  const printFrameRef = useRef(null);
  const pageElsRef = useRef(new Map());
  const scrollSyncLockRef = useRef(false);
  const [fileSource, setFileSource] = useState(null);
  const [docReady, setDocReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);
  const localBlobRef = useRef("");
  /** Kept for download / print (prefer over re-fetch). */
  const pdfBlobRef = useRef(null);
  const loadGenRef = useRef(0);

  const revokeLocalBlob = useCallback(() => {
    if (!localBlobRef.current) return;
    URL.revokeObjectURL(localBlobRef.current);
    localBlobRef.current = "";
  }, []);

  const toPdfBlob = useCallback((blob) => {
    if (!blob) return null;
    if (blob.type && /pdf/i.test(blob.type)) return blob;
    return new Blob([blob], { type: "application/pdf" });
  }, []);

  useEffect(() => {
    const url = String(fileUrl || "").trim();
    const gen = ++loadGenRef.current;
    let cancelled = false;

    setLoadError("");
    setDocReady(false);
    setNumPages(0);
    setPageNumber(1);
    setScale(1);
    pdfBlobRef.current = null;
    pageElsRef.current = new Map();

    if (!url) {
      setFileSource(null);
      revokeLocalBlob();
      return undefined;
    }

    // Point PDF.js at the URL immediately (proxy / blob / remote).
    setFileSource((prev) => (prev === url ? prev : url));

    const warmBlobForActions = async () => {
      try {
        const response = await fetch(url, {
          mode: "cors",
          credentials: "omit",
        });
        if (!response.ok || cancelled || gen !== loadGenRef.current) return;
        const blob = toPdfBlob(await response.blob());
        if (cancelled || gen !== loadGenRef.current) return;
        pdfBlobRef.current = blob;

        if (!url.startsWith("blob:") && !url.startsWith("data:")) {
          revokeLocalBlob();
          localBlobRef.current = URL.createObjectURL(blob);
        }
      } catch {
        /* download/print can fall back to fileSource */
      }
    };

    void warmBlobForActions();

    return () => {
      cancelled = true;
      revokeLocalBlob();
      pdfBlobRef.current = null;
    };
  }, [fileUrl, revokeLocalBlob, toPdfBlob]);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const updateWidth = () => {
      const next = Math.floor(node.clientWidth - 24);
      if (next <= 0) return;
      setPageWidth((prev) => {
        if (!prev) return next;
        if (Math.abs(prev - next) < WIDTH_STABLE_PX) return prev;
        return next;
      });
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /** Keep toolbar page indicator in sync while scrolling. */
  useEffect(() => {
    const root = wrapRef.current;
    if (!root || !numPages || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollSyncLockRef.current) return;
        entries.forEach((entry) => {
          const page = Number(entry.target.getAttribute("data-page"));
          if (!page) return;
          ratios.set(page, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        let bestPage = 1;
        let bestRatio = -1;
        ratios.forEach((ratio, page) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = page;
          }
        });
        if (bestRatio > 0) {
          setPageNumber((prev) => (prev === bestPage ? prev : bestPage));
        }
      },
      {
        root,
        threshold: [0.15, 0.35, 0.55, 0.75],
      },
    );

    pageElsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [numPages, docReady, pageWidth, scale]);

  const onDocumentLoadSuccess = useCallback(({ numPages: next }) => {
    setNumPages(next || 0);
    setPageNumber(1);
    setLoadError("");
    setDocReady(true);
  }, []);

  const onDocumentLoadError = useCallback(
    (error) => {
      setLoadError(error?.message || "Unable to display this PDF.");
      setNumPages(0);
      setDocReady(false);
      onLoadError?.(error);
    },
    [onLoadError],
  );

  const setPageEl = useCallback((page, el) => {
    if (el) pageElsRef.current.set(page, el);
    else pageElsRef.current.delete(page);
  }, []);

  const goToPage = useCallback(
    (next) => {
      if (!numPages) return;
      const page = Math.min(Math.max(1, next), numPages);
      setPageNumber(page);
      const el = pageElsRef.current.get(page);
      const root = wrapRef.current;
      if (!el || !root) return;
      scrollSyncLockRef.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        scrollSyncLockRef.current = false;
      }, 450);
    },
    [numPages],
  );

  const zoomBy = useCallback((delta) => {
    setScale((prev) => {
      const next = Math.round((prev + delta) * 100) / 100;
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
    });
  }, []);

  const resetZoom = useCallback(() => setScale(1), []);

  const displayTitle = useMemo(
    () => String(fileTitle || "Document").trim() || "Document",
    [fileTitle],
  );

  const downloadName = useMemo(() => {
    const name = displayTitle;
    return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
  }, [displayTitle]);

  const pageList = useMemo(() => {
    if (!numPages) return [];
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages]);

  const resolvePdfObjectUrl = useCallback(async () => {
    if (pdfBlobRef.current) {
      return {
        url: URL.createObjectURL(pdfBlobRef.current),
        revoke: true,
      };
    }
    if (fileSource) {
      return { url: fileSource, revoke: false };
    }
    const url = String(fileUrl || "").trim();
    if (!url) return null;
    const response = await fetch(url, {
      mode: "cors",
      credentials: "omit",
    });
    if (!response.ok) throw new Error("Unable to load PDF for download/print.");
    const blob = toPdfBlob(await response.blob());
    pdfBlobRef.current = blob;
    return { url: URL.createObjectURL(blob), revoke: true };
  }, [fileSource, fileUrl, toPdfBlob]);

  const handleDownload = useCallback(async () => {
    try {
      const resolved = await resolvePdfObjectUrl();
      if (!resolved?.url) return;
      const link = document.createElement("a");
      link.href = resolved.url;
      link.download = downloadName;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (resolved.revoke) {
        window.setTimeout(() => URL.revokeObjectURL(resolved.url), 1500);
      }
    } catch (error) {
      console.warn("PdfJsPreviewViewer: download failed", error?.message || error);
    }
  }, [downloadName, resolvePdfObjectUrl]);

  const handlePrint = useCallback(async () => {
    try {
      const resolved = await resolvePdfObjectUrl();
      if (!resolved?.url) return;

      let frame = printFrameRef.current;
      if (!frame) {
        frame = document.createElement("iframe");
        frame.setAttribute("title", "Print PDF");
        frame.style.cssText =
          "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
        document.body.appendChild(frame);
        printFrameRef.current = frame;
      }

      const runPrint = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } catch (error) {
          console.warn("PdfJsPreviewViewer: print failed", error?.message || error);
          window.open(resolved.url, "_blank", "noopener,noreferrer");
        } finally {
          if (resolved.revoke) {
            window.setTimeout(() => URL.revokeObjectURL(resolved.url), 60_000);
          }
        }
      };

      frame.onload = () => {
        window.setTimeout(runPrint, 250);
      };
      frame.src = resolved.url;
    } catch (error) {
      console.warn("PdfJsPreviewViewer: print prepare failed", error?.message || error);
    }
  }, [resolvePdfObjectUrl]);

  useEffect(
    () => () => {
      const frame = printFrameRef.current;
      if (frame?.parentNode) frame.parentNode.removeChild(frame);
      printFrameRef.current = null;
    },
    [],
  );

  const actionsDisabled = !fileSource || Boolean(loadError) || !docReady;
  const showPages = Boolean(fileSource && !loadError && pageWidth > 0);
  const showLoadingOverlay = Boolean(fileSource && !loadError && !docReady);
  const renderedWidth = Math.floor(pageWidth * scale);

  return (
    <div
      className={`aq-pdfjs ${className}`.trim()}
      style={{ width: "100%", height: "100%", minHeight: 420 }}
    >
      <div className="aq-pdfjs__toolbar" role="toolbar" aria-label="PDF preview">
        <div className="aq-pdfjs__toolbar-left" title={displayTitle}>
          <span className="aq-pdfjs__title">{displayTitle}</span>
        </div>
        <div className="aq-pdfjs__toolbar-center">
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => goToPage(pageNumber - 1)}
            disabled={pageNumber <= 1}
            title="Previous page"
            aria-label="Previous page"
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <span className="aq-pdfjs__page">
            {numPages ? `${pageNumber} / ${numPages}` : "—"}
          </span>
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => goToPage(pageNumber + 1)}
            disabled={!numPages || pageNumber >= numPages}
            title="Next page"
            aria-label="Next page"
          >
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
          <span className="aq-pdfjs__sep" aria-hidden="true" />
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => zoomBy(-SCALE_STEP)}
            disabled={scale <= MIN_SCALE || !docReady}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <i className="fas fa-minus" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="aq-pdfjs__zoom-label"
            onClick={resetZoom}
            title="Reset zoom"
            disabled={!docReady}
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => zoomBy(SCALE_STEP)}
            disabled={scale >= MAX_SCALE || !docReady}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <i className="fas fa-plus" aria-hidden="true" />
          </button>
        </div>
        <div className="aq-pdfjs__toolbar-right">
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => void handleDownload()}
            disabled={actionsDisabled}
            title="Download PDF"
            aria-label="Download PDF"
          >
            <i className="fas fa-download" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="aq-pdfjs__btn"
            onClick={() => void handlePrint()}
            disabled={actionsDisabled}
            title="Print PDF"
            aria-label="Print PDF"
          >
            <i className="fas fa-print" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="aq-pdfjs__canvas-wrap" ref={wrapRef}>
        {!fileSource && !loadError ? (
          <div className="aq-pdfjs__empty">Loading PDF…</div>
        ) : null}
        {loadError ? (
          <div className="aq-pdfjs__empty aq-pdfjs__empty--error">{loadError}</div>
        ) : null}
        {showPages ? (
          <Document
            key={fileSource}
            file={fileSource}
            loading={null}
            error={
              <div className="aq-pdfjs__empty aq-pdfjs__empty--error">
                Unable to display this PDF.
              </div>
            }
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
          >
            {pageList.map((page) => (
              <div
                key={page}
                className="aq-pdfjs__page-block"
                data-page={page}
                ref={(el) => setPageEl(page, el)}
              >
                <Page
                  pageNumber={page}
                  width={renderedWidth}
                  renderAnnotationLayer
                  renderTextLayer
                  loading={
                    <div className="aq-pdfjs__page-placeholder">
                      Page {page}…
                    </div>
                  }
                />
              </div>
            ))}
          </Document>
        ) : null}
        {showLoadingOverlay ? (
          <div className="aq-pdfjs__loading-overlay" aria-live="polite">
            Loading preview…
          </div>
        ) : null}
      </div>
    </div>
  );
}
